/**
 * Plush media zoom — pinch/wheel/double-tap zoom inside the product lightbox.
 *
 * Without this the modal image is a plain <img>, so a pinch gesture zooms the
 * whole page instead of the photo. Here the gesture is captured on the modal and
 * applied as a CSS transform to the image itself:
 *
 *   - Pinch (touch)        : zoom around the midpoint of the two fingers
 *   - Wheel / trackpad     : zoom around the cursor
 *   - Double click / tap   : toggle between fit and 2.5x
 *   - Drag (while zoomed)  : pan
 *
 * Scale is clamped to 1–5x and the image snaps back to centre at 1x. State is
 * per-image and resets whenever the modal closes.
 */
(function () {
  'use strict';

  var MIN = 1;
  var MAX = 5;

  function setup(modal) {
    if (modal.dataset.plushZoom === '1') return;
    modal.dataset.plushZoom = '1';

    var state = { img: null, scale: 1, x: 0, y: 0 };
    var drag = null;
    var pinch = null;

    function apply() {
      if (!state.img) return;
      // At 1x we hand the image back untouched so it stays exactly where the
      // theme's own layout puts it.
      if (state.scale <= MIN + 0.001) {
        state.scale = MIN;
        state.x = 0;
        state.y = 0;
      }
      state.img.style.transform =
        'translate(' + state.x + 'px,' + state.y + 'px) scale(' + state.scale + ')';
      state.img.style.cursor = state.scale > MIN ? 'grab' : 'zoom-in';
      modal.classList.toggle('plush-zoomed', state.scale > MIN);
    }

    function endDrag() {
      if (swipe) {
        // Settle on whichever image the drag landed nearest, then hand snapping
        // back to CSS.
        var c = modal.querySelector('.product-media-modal__content');
        if (c && c.clientWidth) {
          var page = Math.round(c.scrollLeft / c.clientWidth);
          // Snap is restored FIRST, then the scroll runs: re-enabling it partway
          // through a smooth animation cancelled the animation and left the
          // carousel parked between two images.
          c.style.scrollSnapType = '';
          c.scrollTo({ left: page * c.clientWidth, behavior: 'smooth' });
        }
        swipe = null;
      }
      if (state.img) state.img.style.cursor = state.scale > MIN ? 'grab' : 'zoom-in';
      if (!drag) return;
      drag = null;
    }

    function reset() {
      if (state.img) {
        state.img.style.transform = '';
        state.img.style.cursor = '';
      }
      state.img = null;
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      modal.classList.remove('plush-zoomed');
    }

    // The modal holds every media item; the one on screen is whichever image the
    // point/gesture landed on.
    function visibleImage() {
      // Whichever image covers the most of the viewport. A vertical-only test
      // matched every slide of the horizontal carousel and returned the first,
      // so a gesture on the padding zoomed an offscreen photo.
      var imgs = modal.querySelectorAll('.product-media-modal__content img');
      var best = null;
      var bestArea = 0;
      for (var i = 0; i < imgs.length; i++) {
        var r = imgs[i].getBoundingClientRect();
        var w = Math.min(r.right, window.innerWidth) - Math.max(r.left, 0);
        var h = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
        if (w <= 0 || h <= 0) continue;
        if (w * h > bestArea) {
          bestArea = w * h;
          best = imgs[i];
        }
      }
      return best;
    }

    function imageAt(target) {
      var img = target && target.closest ? target.closest('.product-media-modal__content img') : null;
      // Gestures often land on the padding around the photo (or on the modal
      // itself); fall back to whichever image is currently on screen.
      if (!img) img = visibleImage();
      if (img && img !== state.img) {
        reset();
        state.img = img;
        img.style.transformOrigin = 'center center';
        img.style.willChange = 'transform';
      }
      return state.img;
    }

    // Zoom about a point so the pixel under the cursor/fingers stays put.
    function zoomAt(clientX, clientY, nextScale) {
      var rect = state.img.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var ratio = nextScale / state.scale;
      state.x = clientX - ratio * (clientX - (cx + state.x)) - cx;
      state.y = clientY - ratio * (clientY - (cy + state.y)) - cy;
      state.scale = nextScale;
      apply();
    }

    function clamp(v) {
      return Math.min(MAX, Math.max(MIN, v));
    }

    modal.addEventListener(
      'wheel',
      function (e) {
        if (!imageAt(e.target)) return;
        e.preventDefault();
        zoomAt(e.clientX, e.clientY, clamp(state.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      },
      { passive: false }
    );

    // Dawn's media-modal closes on any mouseup/pointerup that isn't inside
    // deferred-media — which fires between the two clicks of a double-click and
    // shuts the lightbox before `dblclick` ever lands. Swallowing those events on
    // the photo keeps the modal open; the backdrop still closes it.
    // Only the *up* events are swallowed: capturing the down events here would
    // stop them before the pan handler below (bound in the bubble phase) sees
    // them, which killed drag-to-pan.
    ['pointerup', 'mouseup'].forEach(function (ev) {
      modal.addEventListener(
        ev,
        function (e) {
          if (e.target.closest && e.target.closest('.product-media-modal__content img')) {
            // Release the pan here: stopping propagation means the window-level
            // mouseup below never runs, which otherwise left the image stuck to
            // the cursor after the button was let go.
            endDrag();
            e.stopPropagation();
          }
        },
        true
      );
    });

    modal.addEventListener('dblclick', function (e) {
      if (!imageAt(e.target)) return;
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, state.scale > MIN ? MIN : 2.5);
    });

    // Touch devices: dblclick is unreliable, so double-tap is detected manually.
    var lastTap = 0;
    var lastTapXY = null;
    modal.addEventListener(
      'touchend',
      function (e) {
        if (e.changedTouches.length !== 1) return;
        var t = e.changedTouches[0];
        if (!imageAt(t.target)) return;
        var now = e.timeStamp;
        var near =
          lastTapXY && Math.hypot(t.clientX - lastTapXY[0], t.clientY - lastTapXY[1]) < 40;
        if (now - lastTap < 320 && near) {
          e.preventDefault();
          e.stopPropagation();
          zoomAt(t.clientX, t.clientY, state.scale > MIN ? MIN : 2.5);
          lastTap = 0;
          lastTapXY = null;
          return;
        }
        lastTap = now;
        lastTapXY = [t.clientX, t.clientY];
      },
      { passive: false }
    );

    modal.addEventListener(
      'touchstart',
      function (e) {
        if (!imageAt(e.target)) return;
        if (e.touches.length === 2) {
          var a = e.touches[0];
          var b = e.touches[1];
          pinch = {
            dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
            scale: state.scale,
          };
          drag = null;
          e.preventDefault();
        } else if (e.touches.length === 1 && state.scale > MIN) {
          drag = { x: e.touches[0].clientX - state.x, y: e.touches[0].clientY - state.y };
          e.preventDefault();
        }
      },
      { passive: false }
    );

    modal.addEventListener(
      'touchmove',
      function (e) {
        if (!state.img) return;
        if (pinch && e.touches.length === 2) {
          var a = e.touches[0];
          var b = e.touches[1];
          var dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
          zoomAt(
            (a.clientX + b.clientX) / 2,
            (a.clientY + b.clientY) / 2,
            clamp(pinch.scale * (dist / pinch.dist))
          );
          e.preventDefault();
        } else if (drag && e.touches.length === 1) {
          state.x = e.touches[0].clientX - drag.x;
          state.y = e.touches[0].clientY - drag.y;
          apply();
          e.preventDefault();
        }
      },
      { passive: false }
    );

    modal.addEventListener('touchend', function (e) {
      if (e.touches.length < 2) pinch = null;
      if (e.touches.length === 0) drag = null;
    });

    // Mouse pan while zoomed — and, at 1x, a drag-to-swipe that scrolls the
    // carousel. Touch devices get that for free from native scrolling; a mouse
    // drag never scrolls a container, so it's driven here.
    var swipe = null;

    function content() {
      return modal.querySelector('.product-media-modal__content');
    }

    // Phones fire compatibility mouse events (mousedown/mousemove/mouseup)
    // after every tap. Letting those arm the mouse drag-to-swipe below left a
    // stale `swipe` behind, and the next tap's synthetic mousemove then
    // rewound the carousel to where that drag "started" — so on touch screens
    // the next arrow could never get past the second photo. Touch already
    // scrolls natively; ignore mouse events that follow a touch.
    var lastTouch = 0;
    modal.addEventListener('touchstart', function () { lastTouch = Date.now(); }, { passive: true, capture: true });
    function fromTouch(e) {
      return (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents) || Date.now() - lastTouch < 1000;
    }

    modal.addEventListener('mousedown', function (e) {
      if (fromTouch(e)) return;
      if (!imageAt(e.target)) return;
      if (state.scale > MIN) {
        drag = { x: e.clientX - state.x, y: e.clientY - state.y };
        state.img.style.cursor = 'grabbing';
      } else {
        var c = content();
        if (!c || c.scrollWidth <= c.clientWidth) return;
        swipe = { startX: e.clientX, startScroll: c.scrollLeft };
        c.style.scrollSnapType = 'none';
        state.img.style.cursor = 'grabbing';
      }
      e.preventDefault();
    });
    window.addEventListener('mousemove', function (e) {
      // A drag only continues while the mouse button is really held; if the
      // matching mouseup was lost (swallowed, or fired outside the window),
      // drop the drag instead of scrolling from a stale start point.
      if ((swipe || drag) && !(e.buttons & 1)) {
        endDrag();
        return;
      }
      if (swipe) {
        var c = content();
        if (c) c.scrollLeft = swipe.startScroll - (e.clientX - swipe.startX);
        return;
      }
      if (!drag || !state.img) return;
      state.x = e.clientX - drag.x;
      state.y = e.clientY - drag.y;
      apply();
    });
    window.addEventListener('mouseup', endDrag);
    // Losing the pointer outside the window must not leave a drag armed either.
    window.addEventListener('blur', endDrag);

    // Closing the lightbox clears the zoom so it reopens fitted.
    modal.addEventListener('click', function (e) {
      if (e.target.closest('.product-media-modal__toggle')) reset();
    });
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') reset();
    });
  }

  function init() {
    document.querySelectorAll('product-modal').forEach(setup);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
